/**
 * Canonical Interview-Question-Bank vocabularies for the editor form.
 *
 * Source of truth is the backend: infrastructure/migrations/seed/
 * generate_interview_seed.js (dimensions/modules/enums) and migration 499
 * (dimension display names, mirrored in quill-engine mirrorScores.DISPLAY_NAMES).
 * These are the SAME 12 dimensions / 9 modules / enum values the backend
 * validates against — kept here so the form only offers valid choices.
 *
 * Ported verbatim from quill-app/quill-admin/src/constants/interview.ts. Renamed
 * from interview.ts to interviewConstants.ts to avoid colliding with the
 * `interview` directory name, and scoped into this directory since it's only
 * consumed by InterviewBankPage / QuestionEditDialog here.
 */

import { Sensitivity, ExpectedDepth } from '../../../services/quillAdminService';

export interface DimensionOption {
  key: string;
  label: string;
}

// Mirror-12 (migration 499). Labels match mirrorScores.DISPLAY_NAMES.
export const DIMENSIONS: DimensionOption[] = [
  { key: 'identity_biography', label: 'Identity & biography' },
  { key: 'relationships_people', label: 'Relationships & people' },
  { key: 'interests_hobbies', label: 'Interests & hobbies' },
  { key: 'preferences', label: 'Preferences (likes/dislikes)' },
  { key: 'values_philosophy', label: 'Values & life philosophy' },
  { key: 'religion', label: 'Religion & spirituality' },
  { key: 'mortality_legacy', label: 'Mortality & legacy view' },
  { key: 'humor_style', label: 'Humor style' },
  { key: 'diction_style', label: 'Diction & lexical style' },
  { key: 'prosody_voice', label: 'Prosody & acoustic voice' },
  { key: 'knowledge_expertise', label: 'Knowledge base & expertise' },
  { key: 'temperament', label: 'Emotional baseline & temperament' },
];

export const DIMENSION_LABEL: Record<string, string> = DIMENSIONS.reduce(
  (acc, d) => {
    acc[d.key] = d.label;
    return acc;
  },
  {} as Record<string, string>
);

// The 9 canonical modules (AVP arc — architecture §4.1).
export const MODULES: string[] = [
  'childhood_origins',
  'family_relationships',
  'work_purpose',
  'beliefs_faith',
  'hardship_resilience',
  'health_aging',
  'values_politics',
  'mortality_legacy',
  'daily_life_tastes_humor',
];

export const SENSITIVITIES: Sensitivity[] = ['none', 'personal', 'charter_sensitive'];
export const EXPECTED_DEPTHS: ExpectedDepth[] = ['fact', 'reflection', 'story'];

export function humanizeModule(m: string): string {
  return m.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export const SENSITIVITY_COLOR: Record<string, 'default' | 'warning' | 'error'> = {
  none: 'default',
  personal: 'warning',
  charter_sensitive: 'error',
};
